import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Copy, Check } from 'lucide-react';
import { useCompanyProfile } from '@/hooks/useCompanyProfile';
import { toast } from '@/hooks/use-toast';

/** Settings body for inbound website-form leads (credentials + example request). */
export function WebFormIntegrationPanel() {
  const { data: company } = useCompanyProfile();
  const [copied, setCopied] = useState<string | null>(null);

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
  const formWebhookUrl = `${supabaseUrl}/functions/v1/receive-form-webhook`;
  const tenantSlug = company?.public_key || '';

  const handleCopy = (value: string, label: string) => {
    navigator.clipboard.writeText(value);
    setCopied(label);
    toast({ title: `${label} copied to clipboard` });
    setTimeout(() => setCopied(null), 2000);
  };

  const exampleRequest = [
    `curl -X POST ${formWebhookUrl} \\`,
    `  -H "x-webhook-secret: your-secret" \\`,
    `  -H "x-tenant-slug: ${tenantSlug || 'YOUR_TENANT_SLUG'}" \\`,
    `  -H "Content-Type: application/json" \\`,
    `  -d '{`,
    `    "customer_name": "John Smith",`,
    `    "customer_phone": "0412345678",`,
    `    "customer_email": "john@example.com",`,
    `    "customer_address": "123 Main Street",`,
    `    "customer_suburb": "Sydney",`,
    `    "customer_state": "NSW",`,
    `    "customer_postcode": "2000",`,
    `    "issue_description": "Need help with blocked drain",`,
    `    "website_source": "plumber-sydney.com.au",`,
    `    "gclid": "Cj0KCQiA...EALw_wcB",`,
    `    "campaign_name": "Google Ads - Sydney"`,
    `  }'`,
  ].join('\n');

  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground">
        Receive leads from website forms, landing pages, and form builders. All form leads land in the Unassigned Pool.
      </p>

      <div className="space-y-4 p-4 bg-muted/50 rounded-lg border">
        <h4 className="font-medium text-sm">Your Credentials</h4>

        <div className="space-y-2">
          <Label htmlFor="tenant-slug">Tenant Slug</Label>
          <div className="flex gap-2">
            <Input id="tenant-slug" value={tenantSlug} readOnly className="font-mono text-sm" />
            <Button
              variant="outline"
              size="icon"
              onClick={() => handleCopy(tenantSlug, 'Tenant slug')}
              title="Copy to clipboard"
            >
              {copied === 'Tenant slug' ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Send this in the <code className="px-1 py-0.5 bg-muted rounded">x-tenant-slug</code> header to identify your account
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="form-webhook-url">Webhook URL</Label>
          <div className="flex gap-2">
            <Input id="form-webhook-url" value={formWebhookUrl} readOnly className="font-mono text-xs" />
            <Button
              variant="outline"
              size="icon"
              onClick={() => handleCopy(formWebhookUrl, 'Form Webhook URL')}
              title="Copy to clipboard"
            >
              {copied === 'Form Webhook URL' ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            </Button>
          </div>
        </div>

        <p className="text-xs text-muted-foreground">
          You also need to include an <code className="px-1 py-0.5 bg-muted rounded">x-webhook-secret</code> header. Use any strong secret you choose and keep it private.
        </p>
      </div>

      <div className="pt-4 border-t">
        <h4 className="font-medium mb-2">Example Request</h4>
        <pre className="bg-muted p-4 rounded-lg text-xs overflow-x-auto">{exampleRequest}</pre>
      </div>
    </div>
  );
}
